import { Injectable } from "@angular/core";
import { ReproductiveHealth, UserInfoService } from "./user.info.service";
import { DiaryService } from "./diary.service";
import { UserProfile } from "../models/user";
import { doc, Firestore, setDoc } from "@angular/fire/firestore";
import { UserService } from "./user.service";
import { ChatOption } from "../models/chat.option";
import { ChatMessage } from "projects/chatapp/src/app/chat-gpt/models/chat-message";
import * as moment from "moment";

export interface OnboardingQuiz {
    flow: string;
    heightUnit: string;
    heightValue: number;
    weightUnit: string;
    weightValue: number;
}


@Injectable({ providedIn: 'root' })
export class OnboardingService {
    constructor(
        private userInfoService: UserInfoService,
        private diaryService: DiaryService,
        private firestore: Firestore,
        private userService: UserService,
    ) { }


    async saveOnboardingQuiz(data: OnboardingQuiz): Promise<void> {
        if (!data) {
            return;
        }
        const ref = doc(this.firestore, 'v0', 'data', 'user', this.userService.user.id, 'onboarding', 'quiz');
        await setDoc(
            ref,
            { ...data },
            { merge: true }
        );
    }

    async getInitialMissingOnboardingData(): Promise<OnboardingQuestionType[]> {
        let userProfile = await this.userInfoService.getUserProfile();
        let userInfoHealth = await this.userInfoService.getReproductiveHealth();
        return this.missingOnboardingData(userProfile, userInfoHealth);
    }

    async missingOnboardingData(userProfile?: UserProfile, userInfoHealth?: ReproductiveHealth): Promise<OnboardingQuestionType[]> {
        let missingData: OnboardingQuestionType[] = [];

        if (userInfoHealth == null || userInfoHealth?.usageReason == null || userInfoHealth?.usageReason.length == 0) {
            missingData.push(OnboardingQuestionType.usageReason)
        }

        let data = await this.getMissingHealthData(userInfoHealth?.usageReason)
        missingData = [...data, ...missingData];

        if (userProfile == null) {
            missingData.push(OnboardingQuestionType.name)
            missingData.push(OnboardingQuestionType.age)
            return missingData;
        }

        if (userProfile.name == null || userProfile.name.length == 0) {
            missingData.push(OnboardingQuestionType.name)
        }

        if (userProfile.dateOfBirth == null || userProfile.dateOfBirth.length == 0 || isNaN(Date.parse(userProfile.dateOfBirth))) {
            missingData.push(OnboardingQuestionType.age)
        }

        return missingData.sort((a, b) => order[a] - order[b]);
    }

    async getMissingHealthData(
        usageReason: string,
    ): Promise<OnboardingQuestionType[]> {
        const healthData: OnboardingQuestionType[] = [];

        const periods = await this.diaryService.getUserPeriods();
        const pregnancies = await this.diaryService.getUserPregnancies();

        const hasPeriod = Array.isArray(periods) && periods.length > 0;
        const hasPregnancy = Array.isArray(pregnancies) && pregnancies.length > 0;


        if (usageReason == null) {
            if (!hasPeriod) {
                healthData.push(OnboardingQuestionType.lastPeriod);
            }
            if (!hasPregnancy) {
                healthData.push(OnboardingQuestionType.dueDate);
            }
            return healthData;
        }

        switch (usageReason) {
            case 'trackMyCycle':
                if (!hasPeriod) healthData.push(OnboardingQuestionType.lastPeriod);
                break;
            case 'toBecomePregnant':
                if (!hasPeriod) healthData.push(OnboardingQuestionType.lastPeriod);
                break;
            case 'trackCurrentPregnancy':
                if (!hasPregnancy) healthData.push(OnboardingQuestionType.dueDate);
                break;
            default:
                break;
        }

        return healthData;
    }


    async saveUserName(name: string) {
        await this.userInfoService.saveUserName(name);
    }

    async saveUsageReason(reason: string) {
        await this.userInfoService.saveUsageReason(reason);
    }

    async saveDateOfBirth(age: string) {
        await this.userInfoService.saveUserDateOfBirth(age);
    }

    async welcome4Message(isOldUser: boolean, hasMissingData: boolean): Promise<ChatMessage[]> {
        if (!hasMissingData) {
            return [];
        }

        let message = new ChatMessage('Let’s start with a few basics so I can tailor everything to you. Ready?');
        message.source = 'assistant';
        message.answer = {
            card: 'C3',
            cta: 'SINGLE',
            options: [new ChatOption('next', "Let's do it")]
        }
        if (isOldUser) {
            message.message = 'I already know quite a bit about you — I just need a little more info to fine-tune your guidance.'
        }

        return [message]
    }

    finishMessages(): ChatMessage[] {
        let message = new ChatMessage('Great, that’s all I needed to know 💜');
        let finishMessage = new ChatMessage('We’re ready to kick off your first session together.');

        let answer = {
            card: 'C1',
            cta: 'NONE',
            options: []
        }

        message.source = 'assistant';
        message.answer = answer;

        finishMessage.source = 'assistant';
        finishMessage.answer = answer;

        return [message, finishMessage]

    }

    async roleUnderstandingMessage(isOldUser: boolean, hasMissingData: boolean) {
        let message = new ChatMessage('My role is to help you understand your body, make sense of your health data, and support both your physical and mental wellbeing.');
        message.source = 'assistant';
        message.answer = {
            card: 'C1',
            cta: 'NONE',
            options: []
        }

        if (isOldUser) {
            message.message = "I’ll help you understand your data and, together with IVY Coach, turn it into guidance that fits you. Are you ready to start?";
            if (!hasMissingData) {
                message.answer = {
                    card: 'C3',
                    cta: 'SINGLE',
                    options: [new ChatOption('next', "Let's do it")]
                }
            }
        }

        return message;
    }

    async generalOnboardingMessages(): Promise<ChatMessage[]> {
        let message = new ChatMessage("Welcome 💜 I’m IVY, your personal health assistant.");
        message.source = 'assistant';
        message.answer = {
            card: 'C1',
            cta: 'NONE',
            options: []
        }
         let profile = await this.userInfoService.getUserProfile();
        let userCreated = moment(profile.userCreated).format("YYYY-MM-DD")
        let isToday = moment(profile.userCreated).isSame(moment(), "day")
        let missingData = await this.missingOnboardingData()

        let isOldUser = userCreated != null && !isToday
        let hasMissingData = !missingData.length

        let roleMessage = await this.roleUnderstandingMessage(isOldUser, hasMissingData);
        let welcome4Message = await this.welcome4Message(isOldUser, hasMissingData)
        return [message, roleMessage, ...welcome4Message];
    }

    currentMessage(type: OnboardingQuestionType): ChatMessage {
        switch (type) {
            case OnboardingQuestionType.name:
                let nameMessage = new ChatMessage("To be more accurate in noticing you patterns, can you tell me what is your name?");
                nameMessage.source = 'assistant';
                nameMessage.answer = {
                    card: 'C1',
                    cta: 'TEXT',
                    options: []
                }
                return nameMessage;
            case OnboardingQuestionType.usageReason:
                let usageReasonMessage = new ChatMessage("What is your main goal for using the app? It is good for me to focus on your main goal.");
                usageReasonMessage.source = 'assistant';
                usageReasonMessage.answer = {
                    card: 'C3',
                    cta: 'SINGLE',
                    options: [
                        new ChatOption('trackMyCycle', 'To track my cycle'),
                        new ChatOption('toTrackCurrentPregnancy', 'To track current pregnancy'),
                        new ChatOption('toBecomePregnant', 'To become pregnant')
                    ]
                }
                return usageReasonMessage;
            case OnboardingQuestionType.age:
                let ageMessage = new ChatMessage("How old are you? Your age helps me analyse your patterns better.");
                ageMessage.source = 'assistant';
                ageMessage.answer = {
                    card: 'C1',
                    cta: 'TEXT',
                    options: []
                }
                return ageMessage;
            case OnboardingQuestionType.lastPeriod:
                let periodMessage = new ChatMessage("When was your last period? Cycle tracking is important in noticing changes.");
                periodMessage.source = 'assistant';
                periodMessage.answer = {
                    card: 'C1',
                    cta: 'TEXT',
                    options: []
                }
                return periodMessage;
            case OnboardingQuestionType.dueDate:
                let dueDateMessage = new ChatMessage("When is your due date?");
                dueDateMessage.source = 'assistant';
                dueDateMessage.answer = {
                    card: 'C1',
                    cta: 'TEXT',
                    options: []
                }
                return dueDateMessage;
        }
    }
}

export enum OnboardingQuestionType {
    name,
    usageReason,
    age,
    lastPeriod,
    dueDate
}

const order: Record<OnboardingQuestionType, number> = {
    [OnboardingQuestionType.name]: 0,
    [OnboardingQuestionType.usageReason]: 1,
    [OnboardingQuestionType.age]: 2,
    [OnboardingQuestionType.lastPeriod]: 3,
    [OnboardingQuestionType.dueDate]: 4,
};